import { EmptyState } from '@/components/patterns/empty-state'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { formatNumber, readProperty } from '@/lib/utils'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { formatClosedDaysPercent, type ClosedDaysChartRow } from './-presentation'

const chartConfig: ChartConfig = {
  pickup_only_days: {
    label: 'Pickup only',
    color: 'var(--color-chart-1)',
  },
  delivery_only_days: {
    label: 'Delivery only',
    color: 'var(--color-chart-2)',
  },
  both_days: {
    label: 'Both',
    color: 'var(--color-chart-3)',
  },
  no_explicit_pause_days: {
    label: 'No explicit pause',
    color: 'var(--color-muted)',
  },
}

const chartAxisTick = {
  fill: 'var(--color-foreground)',
  fontSize: 11,
  fontWeight: 500,
}

export function ClosedDaysCoverageChart({
  rows,
  selectedDayCount,
}: {
  rows: ClosedDaysChartRow[]
  selectedDayCount: number
}) {
  if (!rows.length) {
    return (
      <EmptyState
        size="compact"
        surface="transparent"
        title="No pickup or delivery services to chart"
      />
    )
  }

  const chartHeight = Math.max(250, rows.length * 52 + 92)

  return (
    <ChartContainer
      config={chartConfig}
      className="w-full aspect-auto [&_.recharts-cartesian-axis-tick_text]:!fill-foreground"
      initialDimension={{ width: 800, height: chartHeight }}
      style={{ height: chartHeight }}
      role="img"
      aria-label={`Explicit pause coverage by location across ${formatNumber(selectedDayCount)} selected days`}
    >
      <BarChart
        accessibilityLayer
        data={rows}
        layout="vertical"
        margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
      >
        <CartesianGrid horizontal={false} />
        <XAxis
          type="number"
          domain={[0, selectedDayCount]}
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          tick={chartAxisTick}
          tickMargin={8}
        />
        <YAxis
          type="category"
          dataKey="location_name"
          tickLine={false}
          axisLine={false}
          width={120}
          tick={chartAxisTick}
          tickMargin={8}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              labelKey="location_name"
              formatter={(value, name, _item, _index, hoveredRow) => {
                // Recharts returns the hovered row untyped; use the report row with its location id.
                const row = rows.find(
                  (candidate) => candidate.location_id === readProperty(hoveredRow, 'location_id')
                )

                return row ? (
                  <div className="flex w-full items-center justify-between gap-4">
                    <span className="text-muted-foreground">
                      {chartConfig[name]?.label ?? name}
                    </span>
                    <span className="font-medium tabular-nums text-foreground">
                      {formatNumber(value)} {value === 1 ? 'day' : 'days'} ·{' '}
                      {formatClosedDaysPercent(Number(value), row.selected_day_count)}
                    </span>
                  </div>
                ) : null
              }}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent className="flex-wrap gap-x-4 gap-y-2" />} />
        <Bar
          dataKey="pickup_only_days"
          stackId="closed-days"
          fill="var(--color-pickup_only_days)"
          isAnimationActive={false}
        />
        <Bar
          dataKey="delivery_only_days"
          stackId="closed-days"
          fill="var(--color-delivery_only_days)"
          isAnimationActive={false}
        />
        <Bar
          dataKey="both_days"
          stackId="closed-days"
          fill="var(--color-both_days)"
          isAnimationActive={false}
        />
        <Bar
          dataKey="no_explicit_pause_days"
          stackId="closed-days"
          fill="var(--color-no_explicit_pause_days)"
          isAnimationActive={false}
          radius={[0, 4, 4, 0]}
        />
      </BarChart>
    </ChartContainer>
  )
}
